import { snapRoundDecimal } from '@calculations/round/decimal';
import { PayloadAction } from '@reduxjs/toolkit';
import { PriceStrategyConfigWithRelations } from '@snap-types/models/price-strategy-config';
import { dec } from '@snap-types/types/decimal';
import { WritableDraft } from 'immer';
import { z } from 'zod/v4';

import { QuoteBuilderStateType } from '../../types/state';
import { EditableQuoteBuilderRequirementField } from '../../types/state/requirement';
import { onPartQuantityChange } from '../calculations/recalculate';
import { buildElementId } from '../utilities/element-id';
import { buildIssueId } from '../utilities/issue-id';
import { deleteIssue, upsertIssue } from '../utilities/issue-managers';

export type RequirementValueChangedParams = {
  quoteLineUuid: string;
  snapOperationUuid: string;
  snapRequirementUuid: string;
  field: EditableQuoteBuilderRequirementField;
  value: string;
  priceStrategyConfigs: PriceStrategyConfigWithRelations[];
};

const validator = z.coerce
  .number({
    error: 'Requirement value is required',
  })
  .positive({ message: 'Requirement value must be greater than 0' });

export function requirementValueChanged(
  state: WritableDraft<QuoteBuilderStateType>,
  action: PayloadAction<RequirementValueChangedParams>,
) {
  const {
    value,
    quoteLineUuid,
    snapOperationUuid,
    snapRequirementUuid,
    field,
    priceStrategyConfigs,
  } = action.payload;

  const elementId = buildElementId({
    uuid: snapRequirementUuid,
    field,
  });

  state.rawFieldValues[elementId] = value;

  const result = validator.safeParse(value);

  const invalidIssueId = buildIssueId({
    area: 'snapRequirement',
    quoteLineUuid,
    snapOperationUuid,
    snapRequirementUuid,
    field,
    type: 'invalid',
  });

  if (!result.success) {
    return upsertIssue(state, invalidIssueId, {
      title: 'Invalid Field',
      description: z.flattenError(result.error).formErrors[0],
      severity: 'error',
      area: 'snapRequirement',
      quoteLineUuid,
      snapOperationUuid,
      snapRequirementUuid,
      type: 'invalid',
      action: {
        type: 'modify',
        elementId,
      },
    });
  }

  deleteIssue(state, invalidIssueId);

  const decimalValue = snapRoundDecimal(dec(result.data));

  const snapWorkOrder = state.quoteLines[quoteLineUuid].snapWorkOrder;

  if (!snapWorkOrder) {
    return;
  }

  snapWorkOrder.snapOperations[snapOperationUuid].snapRequirements[
    snapRequirementUuid
  ][field] = decimalValue;

  for (const quotePriceUuid of state.quoteLines[quoteLineUuid]
    .quotePriceUuids) {
    onPartQuantityChange(state, {
      quoteLineUuid,
      quotePriceUuid,
      quantity:
        state.quoteLines[quoteLineUuid].quotePrices[quotePriceUuid].quantity,
      priceStrategyConfigs,
    });
  }
}
